<template>
  <div class="dp" :class="{ 'dp-bare': bare }">
    <button
      ref="trigger"
      type="button"
      class="dp-trigger"
      :class="{ 'is-open': open, 'is-empty': !hasValue }"
      :disabled="disabled"
      aria-haspopup="dialog"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="triggerAria"
      @click="toggle"
    >
      <svg class="dp-ic" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
      </svg>
      <span class="dp-tlabel" v-if="triggerPreset">{{ triggerPreset }}</span>
      <span class="dp-ttext">{{ triggerText }}</span>
      <svg class="dp-caret" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        ref="pop"
        class="dp-pop"
        :class="{ 'dp-narrow': narrow, 'dp-noside': !(range && presets) }"
        :style="popStyle"
        role="dialog"
        :aria-label="dialogLabel"
        @keydown="onPopKeydown"
      >
        <!-- Google Ads' menu: the presets, the two "N days up to …" spans -->
        <div class="dp-side" v-if="range && presets">
          <button type="button" class="dp-preset" :class="{ 'is-on': activePreset === 'custom' }" @click="startCustom">Custom</button>
          <div class="dp-rule"></div>
          <div v-for="p in presetRows" :key="p.id" class="dp-preset-row">
            <button
              type="button"
              class="dp-preset"
              :class="{ 'is-on': activePreset === p.id }"
              :disabled="p.off"
              :title="p.off ? offTitle : null"
              @click="pickPreset(p.id)"
            >{{ p.label }}</button>
            <button
              v-if="p.weekly"
              type="button"
              class="dp-variant"
              :aria-label="'Weeks that start on Sunday or Monday: ' + p.label"
              :aria-expanded="flyout === p.id ? 'true' : 'false'"
              @click="flyout = flyout === p.id ? '' : p.id"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
            </button>
            <div v-if="flyout === p.id" class="dp-flyout" role="menu">
              <button
                v-for="ws in [0, 1]"
                :key="ws"
                type="button"
                role="menuitemradio"
                :aria-checked="weekPref === ws ? 'true' : 'false'"
                class="dp-flyout-item"
                :class="{ 'is-on': weekPref === ws }"
                @click="pickVariant(p.id, ws)"
              >{{ variantLabel(p.id, ws) }}</button>
            </div>
          </div>
          <button
            v-if="earliestDay"
            type="button"
            class="dp-preset"
            :class="{ 'is-on': activePreset === 'allTime' }"
            :disabled="allTimeOff"
            :title="allTimeOff ? offTitle : null"
            @click="pickAllTime"
          >All time</button>
          <div class="dp-rule"></div>
          <label class="dp-n" :class="{ 'is-on': activePreset === 'upToToday' }">
            <input
              class="dp-n-input"
              type="number"
              min="1"
              :max="nMax"
              inputmode="numeric"
              aria-label="Number of days up to today"
              v-model.number="nToday"
              @focus="$event.target.select()"
              @input="pickDays('today')"
            />
            <span>days up to today</span>
          </label>
          <label class="dp-n" :class="{ 'is-on': activePreset === 'upToYesterday' }">
            <input
              class="dp-n-input"
              type="number"
              min="1"
              :max="nMax"
              inputmode="numeric"
              aria-label="Number of days up to yesterday"
              v-model.number="nYesterday"
              @focus="$event.target.select()"
              @input="pickDays('yesterday')"
            />
            <span>days up to yesterday</span>
          </label>
        </div>

        <div class="dp-main">
          <!-- A single date's quick picks, above its calendar -->
          <div class="dp-chips" v-if="!range && quickPicks.length">
            <button
              v-for="q in quickPicks"
              :key="q.label"
              type="button"
              class="dp-chip"
              :class="{ 'is-on': q.on }"
              :disabled="q.off"
              @click="pickSingle(q.day)"
            >{{ q.label }}</button>
          </div>

          <div class="dp-fields" v-if="range">
            <label class="dp-field" :class="{ 'is-focus': editing === 'from', 'is-bad': fieldError === 'from' }">
              <span class="dp-flabel">Start date</span>
              <input
                ref="fieldFrom"
                class="dp-finput"
                type="text"
                autocomplete="off"
                spellcheck="false"
                v-model="fieldFrom"
                :aria-invalid="fieldError === 'from' ? 'true' : 'false'"
                @focus="editing = 'from'"
                @blur="commitField('from')"
                @keydown.enter.prevent="commitField('from', true)"
              />
            </label>
            <span class="dp-dash" aria-hidden="true">–</span>
            <label class="dp-field" :class="{ 'is-focus': editing === 'to', 'is-bad': fieldError === 'to' }">
              <span class="dp-flabel">End date</span>
              <input
                ref="fieldTo"
                class="dp-finput"
                type="text"
                autocomplete="off"
                spellcheck="false"
                v-model="fieldTo"
                :aria-invalid="fieldError === 'to' ? 'true' : 'false'"
                @focus="editing = 'to'"
                @blur="commitField('to')"
                @keydown.enter.prevent="commitField('to', true)"
              />
            </label>
          </div>
          <div class="dp-fields" v-else-if="unit === 'day'">
            <label class="dp-field dp-field-one" :class="{ 'is-focus': editing === 'from', 'is-bad': fieldError === 'from' }">
              <span class="dp-flabel">{{ fieldLabel }}</span>
              <input
                ref="fieldFrom"
                class="dp-finput"
                type="text"
                autocomplete="off"
                spellcheck="false"
                v-model="fieldFrom"
                :aria-invalid="fieldError === 'from' ? 'true' : 'false'"
                @focus="editing = 'from'"
                @blur="commitField('from')"
                @keydown.enter.prevent="commitField('from', true)"
              />
            </label>
          </div>

          <div class="dp-cal">
            <div class="dp-nav">
              <button
                type="button"
                class="dp-jump-btn"
                :aria-expanded="jumpOpen ? 'true' : 'false'"
                aria-label="Go to a month"
                @click="toggleJump"
              >
                {{ headerLabel }}
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10l5 5 5-5z" /></svg>
              </button>
              <div class="dp-arrows">
                <button type="button" class="dp-arrow" aria-label="Previous month" :disabled="!canStepBack" @click="stepMonth(-1)">
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
                <button type="button" class="dp-arrow" aria-label="Next month" :disabled="!canStepForward" @click="stepMonth(1)">
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
                </button>
              </div>
            </div>
            <div class="dp-dow" aria-hidden="true">
              <span v-for="(d, i) in weekdayLetters" :key="i">{{ d }}</span>
            </div>
            <div
              ref="scroll"
              class="dp-scroll"
              role="grid"
              :aria-label="dialogLabel"
              @scroll.passive="onScroll"
              @mouseleave="hover = ''"
            >
              <div :style="{ height: padTop + 'px' }"></div>
              <div v-for="m in visibleMonths" :key="m.key" class="dp-month" :style="{ height: m.height + 'px' }" role="rowgroup">
                <div v-if="!m.inline" class="dp-row" role="presentation"><span class="dp-mlabel">{{ m.label }}</span></div>
                <div v-for="(week, wi) in m.weeks" :key="wi" class="dp-row" role="row">
                  <span
                    v-if="wi === 0 && m.inline"
                    class="dp-mlabel dp-mlabel-in"
                    :style="{ width: m.lead * cellSize + 'px' }"
                    role="presentation"
                  >{{ m.label }}</span>
                  <template v-for="(day, ci) in week" :key="ci">
                    <span v-if="day" class="dp-cell" :class="cellClass(day)" role="gridcell" :aria-selected="isPicked(day) ? 'true' : 'false'">
                      <button
                        type="button"
                        class="dp-day"
                        :data-day="day"
                        :tabindex="day === focusDay ? 0 : -1"
                        :disabled="isOff(day)"
                        :aria-label="dayLabel(day)"
                        @click="pickDay(day)"
                        @mouseenter="hover = day"
                        @focus="focusDay = day"
                        @keydown="onDayKeydown($event, day)"
                      >{{ +day.slice(8) }}</button>
                    </span>
                    <span v-else-if="!(wi === 0 && m.inline)" class="dp-cell" role="presentation"></span>
                  </template>
                </div>
              </div>
              <div :style="{ height: padBottom + 'px' }"></div>
            </div>

            <div v-if="jumpOpen" ref="jump" class="dp-jump" role="dialog" aria-label="Go to a month">
              <div v-for="y in jumpYears" :key="y.year" class="dp-jump-year" :data-year="y.year">
                <div class="dp-jump-title">{{ y.year }}</div>
                <div class="dp-jump-grid">
                  <button
                    v-for="mo in y.months"
                    :key="mo.key"
                    type="button"
                    class="dp-jump-month"
                    :class="{ 'is-on': mo.key === headerMonth }"
                    :disabled="mo.off"
                    @click="jumpTo(mo.key)"
                  >{{ mo.label }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="dp-foot" v-if="range || clearable">
          <span class="dp-note" role="status">{{ footNote }}</span>
          <button v-if="clearable && !range" type="button" class="dp-btn" @click="clearSingle">Clear</button>
          <template v-if="range">
            <button type="button" class="dp-btn" @click="cancel">Cancel</button>
            <button type="button" class="dp-btn dp-btn-primary" :disabled="!canApply" @click="apply">Apply</button>
          </template>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import {
  isDay,
  isMonth,
  today as todayDay,
  addDays,
  spanDays,
  weekday,
  startOfWeek,
  daysInMonth,
  startOfMonth,
  endOfMonth,
  monthOf,
  addMonths,
  monthsBetween,
  minDay,
  maxDay,
  clampDay,
  makeDay,
  formatDay,
  formatDayLong,
  formatMonth,
  formatRange,
  parseDay,
  PRESETS,
  daysUpTo,
  MONTH_SHORT,
} from "@/utils/dateRange";

const CELL = 40; // a day's square, px
const MONTH_GAP = 12; // space after each month, px
const LETTERS = ["S", "M", "T", "W", "T", "F", "S"];
const WEEK_PREF_KEY = "connect.dates.weekStart";
const NARROW_PX = 640;
const YEARS_BACK = 10; // how far back the calendar scrolls when the page sets no `min`
const YEARS_AHEAD = 5; // and forward, for a form that takes future dates

/** The week the viewer last chose for "This week" / "Last week" (▸), or `fallback` when none. */
function readWeekPref(fallback) {
  try {
    const v = localStorage.getItem(WEEK_PREF_KEY);
    return v === "1" ? 1 : v === "0" ? 0 : fallback;
  } catch (e) {
    return fallback;
  }
}

function saveWeekPref(value) {
  try {
    localStorage.setItem(WEEK_PREF_KEY, String(value));
  } catch (e) {
    /* a private window: the choice lasts until the page closes */
  }
}

/**
 * The panel's calendar, in Google Ads' shape (owner, 2026-10-23): one button that names the
 * dates, opening a menu of presets beside a calendar that scrolls through the months, the
 * start and end typed or clicked, and nothing changes until Apply.
 *
 *   <DatePicker range v-model:start="from" v-model:end="to" @change="load" />
 *       a report's range; :max-days caps it, :earliest adds "All time", :min / :max bound it
 *       (days after today are off unless `future`). :week-start="1" makes "This week" and
 *       "Last week" Monday weeks until the viewer picks the other kind with ▸ (kept per browser).
 *   <DatePicker v-model="day" />                 one day; picked at once, no Apply
 *   <DatePicker unit="week" :week-start="1" v-model="day" />   a week, given as its first day
 *   <DatePicker unit="month" v-model="month" />  a month, "YYYY-MM"
 *
 * Days are "YYYY-MM-DD" text (src/utils/dateRange.js). `change` fires after the v-models,
 * so a page can reload from its own data in the handler.
 */
export default {
  name: "DatePicker",
  props: {
    range: { type: Boolean, default: false },
    start: { type: String, default: "" },
    end: { type: String, default: "" },
    modelValue: { type: String, default: "" },
    unit: { type: String, default: "day", validator: (v) => ["day", "week", "month"].includes(v) },
    min: { type: String, default: "" },
    max: { type: String, default: "" },
    future: { type: Boolean, default: false },
    maxDays: { type: Number, default: 0 },
    earliest: { type: String, default: "" },
    weekStart: { type: Number, default: 0 },
    presets: { type: Boolean, default: true },
    clearable: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    bare: { type: Boolean, default: false },
    label: { type: String, default: "" },
    placeholder: { type: String, default: "" },
  },
  emits: ["update:start", "update:end", "update:modelValue", "change"],
  data() {
    return {
      open: false,
      narrow: false,
      popStyle: {},
      t: todayDay(),
      draftFrom: "",
      draftTo: "",
      anchor: "", // the first day clicked, while the second is awaited
      hover: "",
      focusDay: "",
      scrollTop: 0,
      viewHeight: 320,
      jumpOpen: false,
      flyout: "",
      weekPref: readWeekPref(this.weekStart === 1 ? 1 : 0),
      nToday: 30,
      nYesterday: 30,
      fieldFrom: "",
      fieldTo: "",
      editing: "",
      fieldError: "",
      cellSize: CELL,
      weekdayLetters: [],
    };
  },
  computed: {
    /** The calendar's first column: the page's week for a week picker, else Sunday as in Google Ads. */
    gridStart() {
      return this.unit === "week" ? this.weekStart : 0;
    },
    minBound() {
      return isDay(this.min) ? this.min : "";
    },
    maxBound() {
      if (isDay(this.max)) return this.max;
      return this.future ? "" : this.t;
    },
    earliestDay() {
      return isDay(this.earliest) ? clampDay(this.earliest, this.minBound, this.maxBound) : "";
    },
    firstMonth() {
      const floor = this.minBound || makeDay(+this.t.slice(0, 4) - YEARS_BACK, 1, 1);
      return monthOf(this.withValues(floor, minDay));
    },
    lastMonth() {
      const ceil = this.maxBound || makeDay(+this.t.slice(0, 4) + YEARS_AHEAD, 12, 31);
      return monthOf(this.withValues(ceil, maxDay));
    },
    /** Two months past the last one with a day to pick, shown greyed as in Google's, so that month
     *  can scroll to the top like any other. */
    tailMonth() {
      return this.maxBound ? addMonths(this.lastMonth, 2) : this.lastMonth;
    },
    months() {
      const out = [];
      const n = monthsBetween(this.firstMonth, this.tailMonth);
      for (let i = 0; i <= n; i++) {
        const key = addMonths(this.firstMonth, i);
        const y = +key.slice(0, 4);
        const mo = +key.slice(5, 7);
        const lead = (weekday(key + "-01") - this.gridStart + 7) % 7;
        const days = daysInMonth(y, mo);
        const weeks = Math.ceil((lead + days) / 7);
        // As in Google's calendar: the month's name sits in the empty start of its first week when
        // there is room for it, else on a row of its own.
        const inline = lead >= 3;
        out.push({ key, y, mo, lead, days, weeks, inline, height: (weeks + (inline ? 0 : 1)) * CELL + MONTH_GAP });
      }
      return out;
    },
    offsets() {
      const out = [0];
      for (const m of this.months) out.push(out[out.length - 1] + m.height);
      return out;
    },
    topIndex() {
      return this.indexAt(this.scrollTop + 1);
    },
    visibleMonths() {
      const from = Math.max(0, this.topIndex - 1);
      const to = Math.min(this.months.length - 1, this.indexAt(this.scrollTop + this.viewHeight) + 1);
      const out = [];
      for (let i = from; i <= to; i++) {
        const m = this.months[i];
        const weeks = [];
        for (let w = 0; w < m.weeks; w++) {
          const row = [];
          for (let c = 0; c < 7; c++) {
            const d = w * 7 + c - m.lead + 1;
            row.push(d >= 1 && d <= m.days ? m.key + "-" + String(d).padStart(2, "0") : "");
          }
          weeks.push(row);
        }
        out.push({ ...m, weeks, label: MONTH_SHORT[m.mo - 1].toUpperCase() + " " + m.y });
      }
      return out;
    },
    padTop() {
      return this.offsets[Math.max(0, this.topIndex - 1)];
    },
    padBottom() {
      const last = Math.min(this.months.length - 1, this.indexAt(this.scrollTop + this.viewHeight) + 1);
      return this.offsets[this.months.length] - this.offsets[last + 1];
    },
    headerMonth() {
      const m = this.months[this.topIndex];
      return m ? m.key : monthOf(this.t);
    },
    headerLabel() {
      return MONTH_SHORT[+this.headerMonth.slice(5, 7) - 1].toUpperCase() + " " + this.headerMonth.slice(0, 4);
    },
    canStepBack() {
      return this.scrollTop > 0;
    },
    canStepForward() {
      return this.headerMonth < this.lastMonth;
    },
    jumpYears() {
      const out = [];
      for (let y = +this.lastMonth.slice(0, 4); y >= +this.firstMonth.slice(0, 4); y--) {
        out.push({
          year: y,
          months: MONTH_SHORT.map((label, i) => {
            const key = y + "-" + String(i + 1).padStart(2, "0");
            return { key, label, off: key < this.firstMonth || key > this.lastMonth };
          }),
        });
      }
      return out;
    },

    // ------------------------------------------------------------ the value
    hasValue() {
      return this.range ? isDay(this.start) && isDay(this.end) : this.unit === "month" ? isMonth(this.modelValue) : isDay(this.modelValue);
    },
    /** A single value as the days it covers. */
    singleSpan() {
      return this.spanOf(this.modelValue);
    },
    triggerText() {
      if (!this.hasValue) return this.placeholder || (this.range ? "Pick dates" : this.unit === "month" ? "Pick a month" : "Pick a date");
      if (this.range) return formatRange(this.start, this.end);
      if (this.unit === "month") return formatMonth(this.modelValue);
      if (this.unit === "week") return formatRange(this.singleSpan[0], this.singleSpan[1]);
      return formatDay(this.modelValue);
    },
    triggerPreset() {
      if (!this.range || !this.hasValue || !this.presets) return "";
      const id = this.presetOf(this.start, this.end);
      return id === "custom" ? "" : this.presetName(id, this.start, this.end);
    },
    triggerAria() {
      const what = this.label || (this.range ? "Dates" : this.unit === "month" ? "Month" : this.unit === "week" ? "Week" : "Date");
      return what + ": " + (this.triggerPreset ? this.triggerPreset + ", " : "") + this.triggerText + ". Change";
    },
    dialogLabel() {
      return this.label || (this.range ? "Choose the dates" : this.unit === "month" ? "Choose a month" : this.unit === "week" ? "Choose a week" : "Choose a date");
    },
    fieldLabel() {
      return this.label || "Date";
    },

    // ----------------------------------------------------------- the menu
    presetRows() {
      return PRESETS.map((p) => {
        const [from, to] = this.presetRange(p.id);
        return { id: p.id, weekly: !!p.weekly, label: p.label(this.weekPref), off: !this.rangeAllowed(from, to) };
      });
    },
    allTimeOff() {
      return !this.earliestDay || !this.rangeAllowed(this.earliestDay, this.t);
    },
    offTitle() {
      return this.maxDays ? "Up to " + this.maxDays + " days here" : "Outside the dates this page has";
    },
    nMax() {
      return this.maxDays || 3650;
    },
    activePreset() {
      return isDay(this.draftFrom) && isDay(this.draftTo) && !this.anchor ? this.presetOf(this.draftFrom, this.draftTo) : "custom";
    },
    canApply() {
      return isDay(this.draftFrom) && isDay(this.draftTo) && !this.fieldError;
    },
    footNote() {
      if (this.fieldError) return "Type a date like " + formatDay(this.t) + ".";
      if (this.anchor) return "Now pick the last day.";
      if (this.maxDays) return "Up to " + this.maxDays + " days at a time.";
      return "";
    },
    quickPicks() {
      if (this.range || !this.presets) return [];
      const t = this.t;
      const picks =
        this.unit === "month"
          ? [
              { label: "This month", day: monthOf(t) },
              { label: "Last month", day: monthOf(addDays(startOfMonth(t), -1)) },
            ]
          : this.unit === "week"
            ? [
                { label: "This week", day: startOfWeek(t, this.weekStart) },
                { label: "Last week", day: addDays(startOfWeek(t, this.weekStart), -7) },
              ]
            : this.future
              ? [
                  { label: "Today", day: t },
                  { label: "Tomorrow", day: addDays(t, 1) },
                ]
              : [
                  { label: "Today", day: t },
                  { label: "Yesterday", day: addDays(t, -1) },
                ];
      return picks.map((q) => {
        const [from, to] = this.spanOf(q.day);
        return { ...q, on: q.day === this.modelValue, off: !this.dayAllowed(from) && !this.dayAllowed(to) };
      });
    },
  },
  watch: {
    draftFrom(v) {
      if (this.editing !== "from") this.fieldFrom = this.range ? formatDay(v) : this.unit === "day" ? formatDay(v) : "";
    },
    draftTo(v) {
      if (this.editing !== "to") this.fieldTo = formatDay(v);
    },
  },
  created() {
    this.weekdayLetters = LETTERS.map((_, i) => LETTERS[(i + this.gridStart) % 7]);
  },
  beforeUnmount() {
    this.unlisten();
  },
  methods: {
    // ------------------------------------------------------------- helpers
    /** A bound pushed out so the page's own values are always on the calendar. */
    withValues(bound, pick) {
      let out = bound;
      const single = this.modelValue || "";
      for (const v of [this.start, this.end, isMonth(single) ? single + "-01" : single]) {
        if (isDay(v)) out = pick(out, v);
      }
      return out;
    },
    indexAt(y) {
      const o = this.offsets;
      let lo = 0;
      let hi = this.months.length - 1;
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (o[mid] <= y) lo = mid;
        else hi = mid - 1;
      }
      return lo;
    },
    /** The days a single value covers: a day, its week or its month. */
    spanOf(value) {
      if (this.unit === "month") {
        if (!isMonth(value)) return ["", ""];
        return [value + "-01", endOfMonth(value + "-01")];
      }
      if (!isDay(value)) return ["", ""];
      if (this.unit === "week") {
        const first = startOfWeek(value, this.weekStart);
        return [first, addDays(first, 6)];
      }
      return [value, value];
    },
    dayAllowed(day) {
      return (!this.minBound || day >= this.minBound) && (!this.maxBound || day <= this.maxBound);
    },
    rangeAllowed(from, to) {
      if (this.maxDays && spanDays(from, to) > this.maxDays) return false;
      return (!this.maxBound || from <= this.maxBound) && (!this.minBound || to >= this.minBound);
    },
    /** A preset's days for today, kept inside the page's bounds. */
    presetRange(id) {
      const p = PRESETS.find((x) => x.id === id);
      const [from, to] = p.range(this.t, this.weekPref);
      return [clampDay(from, this.minBound, this.maxBound), clampDay(to, this.minBound, this.maxBound)];
    },
    presetOf(from, to) {
      for (const p of PRESETS) {
        const [a, b] = this.presetRange(p.id);
        if (a === from && b === to) return p.id;
      }
      if (this.earliestDay && from === this.earliestDay && to === this.t) return "allTime";
      if (to === this.t) return "upToToday";
      if (to === addDays(this.t, -1)) return "upToYesterday";
      return "custom";
    },
    presetName(id, from, to) {
      if (id === "allTime") return "All time";
      if (id === "upToToday") return spanDays(from, to) + (spanDays(from, to) === 1 ? " day" : " days") + " up to today";
      if (id === "upToYesterday") return spanDays(from, to) + (spanDays(from, to) === 1 ? " day" : " days") + " up to yesterday";
      const p = PRESETS.find((x) => x.id === id);
      return p ? p.label(this.weekPref) : "";
    },
    variantLabel(id, ws) {
      return PRESETS.find((x) => x.id === id).label(ws);
    },
    dayLabel(day) {
      let text = formatDayLong(day);
      if (day === this.t) text += ", today";
      return text;
    },

    // ------------------------------------------------------- the calendar
    isOff(day) {
      if (!this.dayAllowed(day)) return true;
      // While the second day is awaited, a capped range keeps the days it cannot reach off.
      if (this.range && this.anchor && this.maxDays) return spanDays(minDay(this.anchor, day), maxDay(this.anchor, day)) > this.maxDays;
      return false;
    },
    /** The days drawn as picked: the draft, or for a single value the value's days. */
    pickedSpan() {
      if (this.range) return [this.draftFrom, this.draftTo];
      return this.spanOf(this.modelValue);
    },
    /** The days drawn as "would be picked": from the first click to the day under the pointer. */
    previewSpan() {
      if (!this.hover || this.isOff(this.hover)) return ["", ""];
      if (this.range) return this.anchor ? [minDay(this.anchor, this.hover), maxDay(this.anchor, this.hover)] : ["", ""];
      return this.unit === "day" ? ["", ""] : this.spanOf(this.unit === "month" ? monthOf(this.hover) : this.hover);
    },
    isPicked(day) {
      const [a, b] = this.pickedSpan();
      return !!a && day >= a && day <= b;
    },
    cellClass(day) {
      const [a, b] = this.anchor ? this.previewSpan() : this.pickedSpan();
      const [pa, pb] = this.anchor ? ["", ""] : this.previewSpan();
      const inPicked = !!a && day >= a && day <= b;
      const inPreview = !inPicked && !!pa && day >= pa && day <= pb;
      const cls = {
        "is-today": day === this.t,
        "is-band": (inPicked && a !== b) || (inPreview && pa !== pb),
        "is-preview": inPreview,
        "is-first": (inPicked && day === a) || (inPreview && day === pa),
        "is-last": (inPicked && day === b) || (inPreview && day === pb),
        "is-end": inPicked && (day === a || day === b),
        "is-anchor": this.anchor === day,
      };
      return cls;
    },
    onScroll() {
      const el = this.$refs.scroll;
      if (el) this.scrollTop = el.scrollTop;
    },
    measure() {
      const el = this.$refs.scroll;
      if (el) this.viewHeight = el.clientHeight || 320;
    },
    /** Put a month at the top of the calendar (no motion: Connect has none). */
    scrollToMonth(key) {
      const i = Math.max(0, Math.min(this.months.length - 1, monthsBetween(this.firstMonth, key)));
      const el = this.$refs.scroll;
      const top = this.offsets[i];
      this.scrollTop = top;
      if (el) el.scrollTop = top;
    },
    /** Bring a day's month into view when it is not, and focus the day. */
    showDay(day, focus) {
      const i = monthsBetween(this.firstMonth, monthOf(day));
      const el = this.$refs.scroll;
      if (el) {
        const top = this.offsets[i];
        const bottom = this.offsets[i + 1] - MONTH_GAP;
        if (top < el.scrollTop || bottom > el.scrollTop + el.clientHeight) this.scrollToMonth(monthOf(day));
      }
      if (!focus) return;
      this.$nextTick(() => {
        const btn = this.$refs.pop && this.$refs.pop.querySelector('.dp-day[data-day="' + day + '"]');
        if (btn) btn.focus();
      });
    },
    stepMonth(step) {
      const i = this.topIndex;
      const aligned = Math.abs(this.offsets[i] - this.scrollTop) < 2;
      const target = step < 0 ? (aligned ? i - 1 : i) : i + 1;
      if (target < 0 || target >= this.months.length) return;
      this.scrollToMonth(this.months[target].key);
    },
    toggleJump() {
      this.jumpOpen = !this.jumpOpen;
      if (!this.jumpOpen) return;
      this.$nextTick(() => {
        const box = this.$refs.jump;
        const row = box && box.querySelector('[data-year="' + this.headerMonth.slice(0, 4) + '"]');
        if (row) box.scrollTop = row.offsetTop - 8;
      });
    },
    jumpTo(key) {
      this.jumpOpen = false;
      this.scrollToMonth(key);
    },

    // ------------------------------------------------------------ picking
    pickDay(day) {
      if (this.isOff(day)) return;
      this.focusDay = day;
      if (!this.range) {
        this.pickSingle(this.unit === "month" ? monthOf(day) : this.unit === "week" ? startOfWeek(day, this.weekStart) : day);
        return;
      }
      this.fieldError = "";
      if (!this.anchor) {
        this.anchor = day;
        this.draftFrom = day;
        this.draftTo = day;
        return;
      }
      this.draftFrom = minDay(this.anchor, day);
      this.draftTo = maxDay(this.anchor, day);
      this.anchor = "";
    },
    setDraft(from, to) {
      this.anchor = "";
      this.fieldError = "";
      this.draftFrom = from;
      this.draftTo = to;
      this.focusDay = from;
      this.scrollToMonth(monthOf(from));
    },
    startCustom() {
      // Google's "Custom": the calendar waits for a first day.
      this.anchor = "";
      this.hover = "";
      this.$nextTick(() => this.showDay(this.focusDay || this.draftFrom || this.t, true));
    },
    pickPreset(id) {
      const [from, to] = this.presetRange(id);
      if (this.rangeAllowed(from, to)) this.setDraft(from, to);
    },
    pickVariant(id, ws) {
      this.weekPref = ws;
      saveWeekPref(ws);
      this.flyout = "";
      this.pickPreset(id);
    },
    pickAllTime() {
      if (!this.allTimeOff) this.setDraft(this.earliestDay, this.t);
    },
    pickDays(upTo) {
      const raw = upTo === "today" ? this.nToday : this.nYesterday;
      const n = Math.round(Number(raw));
      if (!n || n < 1) return; // still being typed
      const capped = Math.min(n, this.nMax);
      if (capped !== n) {
        if (upTo === "today") this.nToday = capped;
        else this.nYesterday = capped;
      }
      const [from, to] = daysUpTo(capped, upTo, this.t);
      this.setDraft(clampDay(from, this.minBound, this.maxBound), clampDay(to, this.minBound, this.maxBound));
    },
    /** A typed date: read on Enter or when the field is left; a date that is not one is said so. */
    commitField(which, viaEnter) {
      if (this.editing === which) this.editing = "";
      const text = which === "from" ? this.fieldFrom : this.fieldTo;
      const year = +(which === "from" ? this.draftFrom || this.t : this.draftTo || this.t).slice(0, 4);
      const day = parseDay(text, year);
      if (!day || !this.dayAllowed(day)) {
        if (viaEnter || String(text).trim()) {
          this.fieldError = which;
          return;
        }
        this.fieldError = "";
        this.resetFields();
        return;
      }
      this.fieldError = "";
      if (day === (which === "from" ? this.draftFrom : this.draftTo)) {
        // Left as it was (a click on the calendar takes the focus away): nothing moves.
        this.resetFields();
        if (viaEnter && which === "from" && this.$refs.fieldTo) this.$refs.fieldTo.focus();
        return;
      }
      if (!this.range) {
        this.pickSingle(day);
        return;
      }
      let from = which === "from" ? day : this.draftFrom || day;
      let to = which === "to" ? day : this.draftTo || day;
      if (from > to) {
        if (which === "from") to = from;
        else from = to;
      }
      if (this.maxDays && spanDays(from, to) > this.maxDays) {
        if (which === "from") to = addDays(from, this.maxDays - 1);
        else from = addDays(to, -(this.maxDays - 1));
      }
      this.anchor = "";
      this.draftFrom = from;
      this.draftTo = to;
      this.resetFields();
      this.focusDay = day;
      this.showDay(day, false);
      if (viaEnter && which === "from" && this.$refs.fieldTo) this.$refs.fieldTo.focus();
    },
    resetFields() {
      this.fieldFrom = this.range || this.unit === "day" ? formatDay(this.draftFrom) : "";
      this.fieldTo = formatDay(this.draftTo);
    },
    pickSingle(value) {
      const [from] = this.spanOf(value);
      if (!from) return;
      this.close(false);
      if (value === this.modelValue) return;
      this.$emit("update:modelValue", value);
      this.$emit("change", value);
    },
    clearSingle() {
      this.close(false);
      if (!this.modelValue) return;
      this.$emit("update:modelValue", "");
      this.$emit("change", "");
    },

    // ------------------------------------------------------- open / close
    toggle() {
      if (this.open) this.cancel();
      else this.show();
    },
    show() {
      if (this.disabled) return;
      this.t = todayDay();
      this.weekdayLetters = LETTERS.map((_, i) => LETTERS[(i + this.gridStart) % 7]);
      if (this.range) {
        this.draftFrom = isDay(this.start) ? this.start : "";
        this.draftTo = isDay(this.end) ? this.end : "";
      } else {
        const [from, to] = this.singleSpan;
        this.draftFrom = from;
        this.draftTo = to;
      }
      const id = this.range && this.draftFrom ? this.presetOf(this.draftFrom, this.draftTo) : "";
      if (id === "upToToday") this.nToday = spanDays(this.draftFrom, this.draftTo);
      if (id === "upToYesterday") this.nYesterday = spanDays(this.draftFrom, this.draftTo);
      this.anchor = "";
      this.hover = "";
      this.flyout = "";
      this.jumpOpen = false;
      this.editing = "";
      this.fieldError = "";
      this.resetFields();
      this.focusDay = clampDay(this.draftFrom || this.t, this.minBound, this.maxBound);
      this.narrow = window.innerWidth <= NARROW_PX;
      this.open = true;
      this.$nextTick(() => {
        this.measure();
        this.scrollToMonth(monthOf(this.draftFrom || this.focusDay));
        this.place();
        this.listen();
        const on = this.$refs.pop && this.$refs.pop.querySelector(".dp-preset.is-on, .dp-chip.is-on");
        if (on && !this.narrow) on.focus();
        else this.showDay(this.focusDay, true);
      });
    },
    close(restoreFocus = true) {
      if (!this.open) return;
      this.open = false;
      this.unlisten();
      if (restoreFocus && this.$refs.trigger) this.$refs.trigger.focus();
    },
    cancel() {
      this.close(true);
    },
    apply() {
      if (!this.canApply) return;
      const from = this.draftFrom;
      const to = this.draftTo;
      this.close(true);
      if (from === this.start && to === this.end) return;
      this.$emit("update:start", from);
      this.$emit("update:end", to);
      this.$emit("change", { start: from, end: to });
    },

    // ------------------------------------------------------- placement
    place() {
      const pop = this.$refs.pop;
      const trigger = this.$refs.trigger;
      if (!pop || !trigger) return;
      this.narrow = window.innerWidth <= NARROW_PX;
      if (this.narrow) {
        this.popStyle = {};
        return;
      }
      const r = trigger.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const w = pop.offsetWidth;
      const h = pop.offsetHeight;
      // Under the trigger, lined up with its nearer edge; above it when there is no room below.
      let left = r.left + r.width / 2 > vw / 2 ? r.right - w : r.left;
      left = Math.max(8, Math.min(left, vw - w - 8));
      let top = r.bottom + 8;
      if (top + h > vh - 8 && r.top - 8 - h >= 8) top = r.top - 8 - h;
      top = Math.max(8, Math.min(top, vh - h - 8));
      this.popStyle = { left: left + "px", top: top + "px" };
    },
    listen() {
      this._onPointer = (e) => {
        const pop = this.$refs.pop;
        const trigger = this.$refs.trigger;
        if ((pop && pop.contains(e.target)) || (trigger && trigger.contains(e.target))) return;
        this.cancel();
      };
      this._onMove = (e) => {
        if (e && e.target && this.$refs.pop && this.$refs.pop.contains(e.target)) return;
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = 0;
          this.measure();
          this.place();
        });
      };
      document.addEventListener("pointerdown", this._onPointer, true);
      window.addEventListener("resize", this._onMove);
      window.addEventListener("scroll", this._onMove, true);
    },
    unlisten() {
      if (this._onPointer) document.removeEventListener("pointerdown", this._onPointer, true);
      if (this._onMove) {
        window.removeEventListener("resize", this._onMove);
        window.removeEventListener("scroll", this._onMove, true);
      }
      if (this._raf) cancelAnimationFrame(this._raf);
      this._onPointer = null;
      this._onMove = null;
      this._raf = 0;
    },

    // --------------------------------------------------------- keyboard
    onPopKeydown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        if (this.jumpOpen) this.jumpOpen = false;
        else if (this.flyout) this.flyout = "";
        else this.cancel();
        return;
      }
      if (e.key !== "Tab") return;
      // Focus stays in the menu while it is open.
      const all = Array.from(this.$refs.pop.querySelectorAll("button, input, [tabindex]")).filter(
        (el) => !el.disabled && el.tabIndex >= 0 && el.offsetParent !== null,
      );
      if (!all.length) return;
      const first = all[0];
      const last = all[all.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    onDayKeydown(e, day) {
      const moves = {
        ArrowLeft: () => addDays(day, -1),
        ArrowRight: () => addDays(day, 1),
        ArrowUp: () => addDays(day, -7),
        ArrowDown: () => addDays(day, 7),
        Home: () => startOfWeek(day, this.gridStart),
        End: () => addDays(startOfWeek(day, this.gridStart), 6),
        PageUp: () => this.sameDayIn(day, -1),
        PageDown: () => this.sameDayIn(day, 1),
      };
      const move = moves[e.key];
      if (!move) return;
      e.preventDefault();
      const next = clampDay(move(), this.minBound || this.firstMonth + "-01", this.maxBound || endOfMonth(this.lastMonth + "-01"));
      this.focusDay = next;
      this.hover = next;
      this.showDay(next, true);
    },
    sameDayIn(day, months) {
      const key = addMonths(monthOf(day), months);
      const last = daysInMonth(+key.slice(0, 4), +key.slice(5, 7));
      return key + "-" + String(Math.min(+day.slice(8), last)).padStart(2, "0");
    },
  },
};
</script>

<style scoped>
/* ── the button that names the dates ─────────────────────────────── */
.dp {
  display: inline-flex;
  min-width: 0;
}
/* --dp-trigger-h: a form sets it to its fields' height so the button lines up with them. */
.dp-trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--s-2, 8px);
  height: var(--dp-trigger-h, 38px);
  max-width: 100%;
  padding: 0 12px;
  font-family: var(--font-main);
  font-size: var(--t-body, 14px);
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  cursor: pointer;
  white-space: nowrap;
}
.dp-trigger:hover:not(:disabled) {
  border-color: var(--border-secondary);
}
.dp-trigger.is-open,
.dp-trigger:focus-visible {
  outline: none;
  border-color: var(--border-focus);
  box-shadow: 0 0 0 2px var(--accent-primary-bg);
}
.dp-trigger:disabled {
  cursor: default;
  opacity: 0.6;
}
.dp-bare .dp-trigger {
  height: 34px;
  padding: 0 6px;
  background: transparent;
  border-color: transparent;
}
.dp-ic,
.dp-caret {
  flex: none;
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.dp-ic {
  color: var(--accent-primary);
}
.dp-caret {
  color: var(--text-muted);
}
.dp-tlabel {
  font-size: var(--t-caption, 12px);
  font-weight: 600;
  color: var(--text-muted);
}
.dp-ttext {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
}
.is-empty .dp-ttext {
  font-weight: 400;
  color: var(--text-muted);
}
/* On a phone the dates themselves get the room. */
@media (max-width: 480px) {
  .dp-tlabel {
    display: none;
  }
}

/* ── the menu ─────────────────────────────────────────────────────── */
/* Above every overlay a form can sit in (the push page's reschedule overlay is at 1000): it is open
   only while someone is picking. */
.dp-pop {
  position: fixed;
  z-index: 2000;
  display: grid;
  grid-template-columns: 252px auto;
  grid-template-rows: minmax(0, 1fr) auto;
  max-height: min(600px, calc(100vh - 16px));
  font-family: var(--font-main);
  font-size: var(--t-body, 14px);
  color: var(--text-primary);
  background: var(--bg-modal, var(--bg-primary));
  border: 1px solid var(--border-primary);
  border-radius: var(--r-lg, 12px);
  box-shadow: var(--shadow-overlay, 0 8px 24px rgba(0, 0, 0, 0.4));
  overflow: hidden;
}
.dp-pop.dp-noside {
  grid-template-columns: auto;
}
.dp-side {
  grid-row: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: var(--s-2, 8px) 0;
  border-right: 1px solid var(--border-primary);
  overflow-y: auto;
}
.dp-rule {
  height: 1px;
  margin: 6px 0;
  background: var(--border-primary);
}
.dp-preset-row {
  position: relative;
  display: flex;
  align-items: stretch;
}
.dp-preset {
  flex: 1;
  min-height: 36px;
  padding: 0 16px;
  font: inherit;
  text-align: left;
  color: var(--text-primary);
  background: transparent;
  border: 0;
  cursor: pointer;
}
.dp-preset:hover:not(:disabled),
.dp-variant:hover {
  background: var(--bg-hover);
}
.dp-preset.is-on {
  font-weight: 600;
  background: var(--accent-primary-bg);
}
.dp-preset:disabled {
  color: var(--text-muted);
  opacity: 0.55;
  cursor: default;
}
.dp-preset:focus-visible,
.dp-variant:focus-visible,
.dp-flyout-item:focus-visible,
.dp-btn:focus-visible,
.dp-chip:focus-visible,
.dp-jump-btn:focus-visible,
.dp-arrow:focus-visible,
.dp-jump-month:focus-visible {
  outline: 2px solid var(--accent-primary);
  outline-offset: -2px;
}
.dp-variant {
  flex: none;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  background: transparent;
  border: 0;
  cursor: pointer;
}
.dp-variant svg {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
}
.dp-flyout {
  position: absolute;
  left: calc(100% - 4px);
  top: 0;
  z-index: 2;
  min-width: 200px;
  padding: 6px 0;
  background: var(--bg-modal, var(--bg-primary));
  border: 1px solid var(--border-primary);
  border-radius: var(--r-md, 8px);
  box-shadow: var(--shadow-overlay, 0 8px 24px rgba(0, 0, 0, 0.4));
}
.dp-flyout-item {
  display: block;
  width: 100%;
  min-height: 34px;
  padding: 0 14px;
  font: inherit;
  text-align: left;
  color: var(--text-primary);
  background: transparent;
  border: 0;
  cursor: pointer;
}
.dp-flyout-item:hover {
  background: var(--bg-hover);
}
.dp-flyout-item.is-on {
  font-weight: 600;
  color: var(--accent-primary);
}
.dp-n {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 0 16px;
  cursor: pointer;
}
.dp-n.is-on {
  font-weight: 600;
  background: var(--accent-primary-bg);
}
.dp-n-input {
  width: 52px;
  height: 32px;
  padding: 0 6px;
  font: inherit;
  font-weight: 400;
  text-align: center;
  color: var(--text-primary);
  background: var(--bg-input);
  border: 1px solid var(--border-secondary);
  border-radius: var(--r-sm, 4px);
  -moz-appearance: textfield;
  appearance: textfield;
}
.dp-n-input::-webkit-outer-spin-button,
.dp-n-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.dp-n-input:focus {
  outline: none;
  border-color: var(--border-focus);
  box-shadow: 0 0 0 2px var(--accent-primary-bg);
}

/* ── the calendar side ────────────────────────────────────────────── */
.dp-main {
  grid-row: 1;
  display: flex;
  flex-direction: column;
  width: 298px; /* the calendar's: the two date fields share it */
  min-height: 0;
  padding: 14px 12px 0;
}
.dp-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 4px 10px;
}
.dp-chip {
  height: 30px;
  padding: 0 12px;
  font: inherit;
  font-size: var(--t-body-sm, 13px);
  color: var(--text-primary);
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 999px;
  cursor: pointer;
}
.dp-chip.is-on {
  font-weight: 600;
  color: var(--accent-primary);
  background: var(--accent-primary-bg);
  border-color: transparent;
}
.dp-chip:disabled {
  opacity: 0.5;
  cursor: default;
}
.dp-fields {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px 12px;
}
.dp-field {
  position: relative;
  flex: 1;
  min-width: 0;
  display: block;
}
.dp-flabel {
  position: absolute;
  top: -8px;
  left: 10px;
  padding: 0 4px;
  font-size: var(--t-caption, 12px);
  line-height: 16px;
  color: var(--text-muted);
  background: var(--bg-modal, var(--bg-primary));
  pointer-events: none;
}
.dp-field.is-focus .dp-flabel {
  color: var(--accent-primary);
}
.dp-field.is-bad .dp-flabel {
  color: var(--danger);
}
.dp-finput {
  box-sizing: border-box;
  width: 100%;
  height: 42px;
  padding: 0 12px;
  font: inherit;
  color: var(--text-primary);
  background: transparent;
  border: 1px solid var(--border-secondary);
  border-radius: var(--r-md, 8px);
  font-variant-numeric: tabular-nums;
}
.dp-finput:focus {
  outline: none;
  border-color: var(--border-focus);
  box-shadow: inset 0 0 0 1px var(--border-focus);
}
.dp-field.is-bad .dp-finput {
  border-color: var(--danger);
  box-shadow: inset 0 0 0 1px var(--danger);
}
.dp-dash {
  color: var(--text-muted);
}
.dp-cal {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}
.dp-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0 6px;
}
.dp-jump-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 32px;
  padding: 0 6px 0 8px;
  font: inherit;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-primary);
  background: transparent;
  border: 0;
  border-radius: var(--r-md, 8px);
  cursor: pointer;
}
.dp-jump-btn:hover,
.dp-arrow:hover:not(:disabled) {
  background: var(--bg-hover);
}
.dp-jump-btn svg {
  width: 20px;
  height: 20px;
  fill: currentColor;
}
.dp-arrows {
  display: flex;
  gap: 4px;
}
.dp-arrow {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
}
.dp-arrow:disabled {
  opacity: 0.35;
  cursor: default;
}
.dp-arrow svg {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.dp-dow {
  display: grid;
  grid-template-columns: repeat(7, 40px);
  height: 30px;
  align-items: center;
  font-size: var(--t-caption, 12px);
  color: var(--text-muted);
  text-align: center;
  border-bottom: 1px solid var(--border-primary);
}
/* 280px of days plus room for a scrollbar that takes space (Windows), so a week never wraps. */
.dp-scroll {
  position: relative;
  flex: 0 1 auto;
  min-height: 160px;
  height: 336px;
  width: 298px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
}
.dp-month {
  box-sizing: border-box;
  padding-bottom: 12px;
}
.dp-row {
  display: flex;
  height: 40px;
}
.dp-mlabel {
  display: flex;
  align-items: center;
  height: 40px;
  padding-left: 8px;
  font-size: var(--t-body-sm, 13px);
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
  color: var(--text-primary);
}
.dp-mlabel-in {
  flex: none;
  box-sizing: border-box;
}
.dp-cell {
  position: relative;
  flex: none;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}
/* The band joins the picked days: a whole cell between them, half a cell at each end. */
.dp-cell.is-band {
  background: var(--accent-primary-bg);
}
.dp-cell.is-band.is-first {
  background: linear-gradient(90deg, transparent 50%, var(--accent-primary-bg) 50%);
}
.dp-cell.is-band.is-last {
  background: linear-gradient(90deg, var(--accent-primary-bg) 50%, transparent 50%);
}
.dp-cell.is-band.is-first.is-last {
  background: none;
}
.dp-cell.is-preview.is-band {
  background: color-mix(in srgb, var(--accent-primary) 8%, transparent);
}
.dp-cell.is-preview.is-band.is-first {
  background: linear-gradient(90deg, transparent 50%, color-mix(in srgb, var(--accent-primary) 8%, transparent) 50%);
}
.dp-cell.is-preview.is-band.is-last {
  background: linear-gradient(90deg, color-mix(in srgb, var(--accent-primary) 8%, transparent) 50%, transparent 50%);
}
.dp-day {
  width: 36px;
  height: 36px;
  padding: 0;
  font: inherit;
  font-variant-numeric: tabular-nums;
  color: var(--text-primary);
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
}
.dp-day:hover:not(:disabled) {
  background: var(--bg-hover);
  box-shadow: inset 0 0 0 1px var(--border-secondary);
}
.is-today .dp-day {
  font-weight: 700;
  box-shadow: inset 0 0 0 1px var(--accent-primary);
}
.is-preview .dp-day {
  color: var(--text-primary);
}
.is-end .dp-day,
.is-anchor .dp-day,
.is-end .dp-day:hover:not(:disabled) {
  font-weight: 600;
  color: #fff;
  background: var(--accent-primary);
  box-shadow: none;
}
.dp-day:disabled {
  color: var(--text-muted);
  opacity: 0.4;
  cursor: default;
}
.dp-day:focus-visible {
  outline: 2px solid var(--accent-primary);
  outline-offset: 1px;
}

/* ── jump to a month ─────────────────────────────────────────────── */
.dp-jump {
  position: absolute;
  inset: 40px 0 0;
  z-index: 3;
  overflow-y: auto;
  padding: 4px 4px 12px;
  background: var(--bg-modal, var(--bg-primary));
}
.dp-jump-title {
  padding: 10px 6px 6px;
  font-weight: 700;
  font-size: var(--t-body-sm, 13px);
  color: var(--text-muted);
}
.dp-jump-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
}
.dp-jump-month {
  height: 34px;
  font: inherit;
  color: var(--text-primary);
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--r-md, 8px);
  cursor: pointer;
}
.dp-jump-month:hover:not(:disabled) {
  background: var(--bg-hover);
}
.dp-jump-month.is-on {
  font-weight: 600;
  color: #fff;
  background: var(--accent-primary);
}
.dp-jump-month:disabled {
  opacity: 0.35;
  cursor: default;
}

/* ── Cancel / Apply ──────────────────────────────────────────────── */
.dp-foot {
  grid-column: 1 / -1;
  grid-row: 2;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid var(--border-primary);
}
.dp-note {
  flex: 1;
  min-width: 0;
  font-size: var(--t-caption, 12px);
  color: var(--text-muted);
}
.dp-btn {
  height: 34px;
  padding: 0 16px;
  font: inherit;
  font-weight: 600;
  color: var(--accent-primary);
  background: transparent;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
}
.dp-btn:hover:not(:disabled) {
  background: var(--accent-primary-bg);
}
.dp-btn-primary {
  color: #fff;
  background: var(--accent-primary);
}
.dp-btn-primary:hover:not(:disabled) {
  background: var(--btn-primary-hover-bg, var(--accent-primary));
}
.dp-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

/* ── a phone: a sheet from the bottom, the presets as one scrolling row ── */
.dp-pop.dp-narrow {
  left: 8px;
  right: 8px;
  bottom: 8px;
  top: auto;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr) auto;
  max-height: calc(100vh - 16px);
}
.dp-narrow .dp-side {
  grid-row: 1;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-right: 0;
  border-bottom: 1px solid var(--border-primary);
  overflow-x: auto;
  overflow-y: hidden;
}
.dp-narrow .dp-rule {
  display: none;
}
.dp-narrow .dp-preset,
.dp-narrow .dp-n {
  flex: none;
  min-height: 32px;
  padding: 0 12px;
  white-space: nowrap;
  border: 1px solid var(--border-primary);
  border-radius: 999px;
}
.dp-narrow .dp-variant,
.dp-narrow .dp-flyout {
  display: none;
}
.dp-narrow .dp-main {
  grid-row: 2;
  width: auto;
  align-items: center;
}
.dp-narrow .dp-fields {
  align-self: stretch;
}
.dp-narrow .dp-foot {
  grid-row: 3;
}
.dp-narrow .dp-scroll {
  height: min(336px, 46vh);
}
</style>
